import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, InputField, Typography, color } from '@kis-technologies/kis-ui';
import { ActionContainer } from 'components/ActionContainer';
import { TextButton } from 'components/TextButton';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { actions as clientsActions, selectors as clientsSelectors } from 'store/clients';
import { validateEmail } from 'utils/validation';
import { useStyles } from './styled';

const SignInView: React.FC = () => {
  const navigate = useNavigate();

  const { login, resetError, getClients, resetClients, setClientId } = useActions({
    login: authActions.login,
    resetError: authActions.resetError,
    getClients: clientsActions.getClients,
    resetClients: clientsActions.resetClients,
    setClientId: clientsActions.setClientId
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const authenticating = useSelector(authSelectors.selectAuthenticating);
  const error = useSelector(authSelectors.selectError);
  const clientId = useSelector(clientsSelectors.selectClientId);
  const clients = useSelector(clientsSelectors.selectClients);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);

  const classes = useStyles();

  // ON COMPONENT MOUNT: RESET CLIENTS IN STORE
  useEffect(() => {
    resetClients();
  }, []);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  useEffect(() => {
    setInvalidMsg(error);
  }, [error]);

  // SET CLIENT ID WHEN ADMIN CHANGES
  useEffect(() => {
    setClientId(admin?.clientId || '');
  }, [admin]);

  // FETCH CLIENTS WHEN CLIENT ID CHANGES
  useEffect(() => {
    if (admin && clientId) getClients();
  }, [clientId]);

  // REDIRECT BASED ON ADMIN ROLE WHEN THE CLIENT IS KNOWN
  useEffect(() => {
    if (!admin || !clientId || !clients.length) return;

    if (admin.resetPasswordRequired) {
      navigate('/resetpassword');
    } else if (admin.role === 'GlobalAdmin') {
      navigate('/customers');
    } else {
      navigate('/kiosks');
    }
  }, [clientId, clients]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail();

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);
    // RESET ERROR TO INITIAL STATE
    resetError();
    // FIRES THE APPROPRIATE FN: SETEMAIL | SETPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onLoginClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // FIRES LOGIN ASYNC FN
    login({ email, password });
  };

  const buttonDisabled = !email.length || !password.length || !!invalidMsg;

  return (
    <ActionContainer
      buttonAction={onLoginClick}
      buttonDisabled={buttonDisabled}
      header="Sign In"
      isLoading={authenticating}
    >
      <Box>
        {/* EMAIL INPUT */}
        <Box className={classes.inputFieldBox}>
          <InputField
            onChange={(e) => onInputChange(setEmail, e.target.value)}
            error={!!invalidMsg}
            helperText={emailValidationFn}
            label="Email"
            type="email"
            placeholder="Your Email"
            size="small"
            sx={{ width: { xs: '262px', sm: '524px' } }}
          />
        </Box>
        {/* PASSWORD INPUT */}
        <Box className={classes.inputFieldBox}>
          <InputField
            onChange={(e) => onInputChange(setPassword, e.target.value)}
            error={!!invalidMsg && !!error}
            label="Password"
            type="password"
            placeholder="Your Password"
            size="small"
            sx={{ width: { xs: '262px', sm: '524px' } }}
          />
        </Box>
        {/* FORGOT PASSWORD BUTTON */}
        <Box className={classes.forgotPasswordBox}>
          <TextButton type="button" onClick={() => navigate('/forgotpassword')}>
            Forgot Password?
          </TextButton>
        </Box>
        {/* ERROR MESSAGE */}
        <Box>{invalidMsg && !authenticating && <Typography color={color.RED}>{invalidMsg}</Typography>}</Box>
      </Box>
    </ActionContainer>
  );
};

export default SignInView;
